<template>
  <div class="timelineComponent">
    <b-card no-body>
      <b-card-header class="d-flex align-items-center">
        <div class="mr-3">Timeline</div>
      </b-card-header>
      <b-card-body>
        <div class="timelineItems">
          <div class="line" />

          <div>
            <div v-for="item in historyItems" :key="item.id" class="timelineItem">
              <b-list-group-item
                button
                variant="info"
                :active="item.id === activeIndex"
                class="rangeItem"
                @click="() => clickHistoryItem(item.id)"
              >
                <div>{{ item.text }}</div>
                <div v-if="item.newBoardCards.length">
                  <SmallCards :cards="item.newBoardCards" />
                </div>
              </b-list-group-item>

              <div v-if="item.action" class="timelineAction">
                <b-badge pill @click="() => changeAction(item.id)">{{ item.action }}</b-badge>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-2">
          <b-button
            v-for="action in possibleActions"
            :key="action"
            @click="() => commitAction(action)"
            class="mr-1"
            size="sm"
            variant="info"
          >
            {{ action }}
          </b-button>
        </div>
      </b-card-body>
    </b-card>
    <b-button v-if="false" @click="save">save</b-button>
  </div>
</template>

<script>
import cloneDeep from "lodash/cloneDeep";
import { ACTIONS, RANGEGROUP_TYPES } from "../constants";
import { boardToGroupedBoard } from "../helpers/cards";
import SmallCards from "./SmallCards";

const currentRangeName = "Now";

export default {
  name: "Timeline",
  components: { SmallCards },
  data() {
    return {
      activeIndex: null,
      history: [],
    };
  },
  created() {
    if (this.savedHistory) {
      this.history = this.savedHistory;
      this.activeIndex = this.history.length - 1;
      this.$emit("activateSnapshot", this.activeHistoryItem.snapshot);
      return;
    }
    // if (window.localStorage["savedRangeProgression"]) {
    //   this.history = JSON.parse(window.localStorage["savedRangeProgression"]);
    //   this.activeIndex = this.history.length - 1;
    //   this.$emit("activateSnapshot", this.activeHistoryItem.snapshot);
    //   return;
    // }
    this.pushNewHistoryItem();
  },

  props: {
    snapshot: { type: Object, required: true },
    comboStringsBySubrange: { type: Object, required: true },
    savedHistory: { type: Array, default: null },
  },

  computed: {
    activeHistoryItem() {
      return this.history[this.activeIndex];
    },
    historyItems() {
      const distributedBoardCards = { flop: false, turn: false, river: false };
      return this.history.map(item => {
        let newBoardCards = [];
        const board = item.snapshot.board;
        const boardLength = board ? board.length : 0;
        if (!distributedBoardCards.flop && boardLength >= 3) {
          distributedBoardCards.flop = true;
          newBoardCards = [...newBoardCards, ...board.slice(0, 3)];
        }
        if (!distributedBoardCards.turn && boardLength >= 4) {
          distributedBoardCards.turn = true;
          newBoardCards = [...newBoardCards, ...board.slice(3, 4)];
        }
        if (!distributedBoardCards.river && boardLength >= 5) {
          distributedBoardCards.river = true;
          newBoardCards = [...newBoardCards, ...board.slice(4, 5)];
        }
        return { ...item, newBoardCards };
      });
    },
    possibleActions() {
      // Don't allow any actions when not at the end of history
      if (this.activeIndex !== this.history.length - 1) return [];
      return [...new Set(Object.values(this.actionsBySubrangeIndex))].filter(action => {
        if (action === ACTIONS.fold) return false;
        if (this.street === "River" && action === ACTIONS.call) return false;
        return true;
      });
    },
    actionsBySubrangeIndex() {
      const actionIndices = Object.keys(this.comboStringsBySubrange);
      const actionsBySubrangeIndex = {};
      for (let i of actionIndices) {
        if (Number(i) === -1) continue;
        const subrange = RANGEGROUP_TYPES[this.snapshot.activeActionSetId].subranges[i];
        actionsBySubrangeIndex[i] = subrange.action;
      }
      return actionsBySubrangeIndex;
    },
    groupedBoard() {
      return boardToGroupedBoard(this.snapshot.board);
    },
    street() {
      if (!this.groupedBoard.flop) return "Preflop";
      if (!this.groupedBoard.turn) return "Flop";
      if (!this.groupedBoard.river) return "Turn";
      return "River";
    },
  },

  methods: {
    pushNewHistoryItem(data = {}) {
      const index = this.history.length;
      const item = {
        text: currentRangeName,
        snapshot: {},
      };
      this.history.push({ ...item, id: index, ...data });
      this.activeIndex = index;
    },
    clickHistoryItem(i) {
      // Before navigating away, commit changes to currently active history item
      this.commitCurrentSnapshot();
      this.activateHistoryItem(i);
    },
    commitCurrentSnapshot() {
      this.$set(this.history, this.activeIndex, {
        ...this.activeHistoryItem,
        snapshot: cloneDeep(this.snapshot),
      });
    },
    activateHistoryItem(i) {
      // If switching to a later history entry and ranges have been changed, make sure those changes
      // are reflected in any later ranges that have already been created
      if (i > this.activeIndex) {
        this.applyChangesToFutureRanges(this.activeIndex, i);
      }
      this.activeIndex = i;
      // The board is only editable if this is the last history entry
      const snapshot = {
        ...this.activeHistoryItem.snapshot,
        canEditBoard: i === this.history.length - 1,
      };
      this.$emit("activateSnapshot", snapshot);
    },
    applyChangesToFutureRanges(indexToApply, lastIndex) {
      if (lastIndex <= indexToApply) return;
      const currentAction = this.history[indexToApply].action;
      const thisRangeData = this.history[indexToApply].snapshot.subrangeByComboString;
      const nextRangeData = this.history[indexToApply + 1].snapshot.subrangeByComboString;
      // Only compare with the chronologically next range to figure out whether future ranges need
      // to be adapted. If there's no discontinuity between the just edited range and the next range,
      // there isn't one between it and any other future ranges either.
      for (let comboString of Object.keys(thisRangeData)) {
        const comboStringAction = this.actionsBySubrangeIndex[thisRangeData[comboString]];
        const comboShouldContinue = comboStringAction === currentAction;
        const comboDoesContinue = nextRangeData[comboString] > -1;
        // If there's no discontinuity, everything is okay
        if (comboShouldContinue === comboDoesContinue) continue;
        // Else adapt future ranges
        for (let i = indexToApply + 1; i <= lastIndex; ++i) {
          const subrangeIndex = comboShouldContinue ? thisRangeData[comboString] : -1;
          this.$set(this.history[i].snapshot.subrangeByComboString, comboString, subrangeIndex);
        }
      }
    },
    commitAction(action) {
      const snapshot = cloneDeep(this.snapshot);
      this.$set(this.history, this.activeIndex, {
        ...this.activeHistoryItem,
        text: this.street,
        snapshot,
        action,
      });
      // Remove all the combos that don't match the selected action from the range
      const filteredSubrangeByComboString = {};
      for (const [comboString, i] of Object.entries(this.snapshot.subrangeByComboString)) {
        const comboStringAction = this.actionsBySubrangeIndex[i];
        filteredSubrangeByComboString[comboString] =
          comboStringAction !== action ? -1 : this.snapshot.subrangeByComboString[comboString];
      }
      const newSnapshot = {
        ...this.snapshot,
        subrangeByComboString: filteredSubrangeByComboString,
        fixedCards: this.snapshot.board.length,
      };
      this.pushNewHistoryItem({ snapshot: newSnapshot });
      this.$emit("activateSnapshot", newSnapshot);
    },
    changeAction(i) {
      const confirmMessage =
        "Changing this action will delete everything that came after! Full decision tree navigation is not yet implemented!";
      if (!confirm(confirmMessage)) return;
      this.history = this.history.slice(0, i + 1);
      this.$set(this.history, i, { ...this.history[i], text: currentRangeName, action: null });
      this.activateHistoryItem(i);
    },
    save() {
      window.localStorage["savedRangeProgression"] = JSON.stringify(this.history);
    },
  },
};
</script>

<style lang="scss" scoped>
.timelineComponent {
  width: 200px;
}
.timelineItems {
  position: relative;
}
.timelineItem {
  margin-top: 15px;
  position: relative;
  &:first-child {
    margin-top: 0;
  }
  .rangeItem {
    border-radius: 5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-right: 5px;
  }
}
.timelineAction {
  display: flex;
  justify-content: center;
  text-transform: uppercase;
  margin-top: 15px;
  cursor: pointer;
}
.line {
  width: 1px;
  background: #aaa;
  height: 100%;
  position: absolute;
  left: 50%;
  top: 0;
}
</style>
